import React, { useEffect, useRef } from "react";

import { ButtonStandard } from "@performyard/py-components";

import { RephraseBlock } from "./RephraseBlock";

import type { RephraseBlockData } from "./types";

type ContentProps = {
	input: string;
	rephrasedData: RephraseBlockData[];
	onInsert: (newAnswer: string) => void;
	handleRephraseDismiss: (index: number) => void;
	handleRetry: (retryText: string) => void;
	handleDismiss: () => void;
	retryDisabled: boolean;
	height?: string;
	isExpanded: boolean;
};

export const Content = ({
	input,
	rephrasedData,
	onInsert,
	handleRephraseDismiss,
	handleRetry,
	handleDismiss,
	retryDisabled,
	height,
	isExpanded,
}: ContentProps): React.ReactElement => {
	const scrollRef = useRef<HTMLDivElement>(null);

	const scrollToBottom = (): void => {
		if (scrollRef.current) {
			scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
		}
	};

	useEffect(() => {
		scrollToBottom();
	}, [rephrasedData]);

	return (
		<div className="mt-2 bg-white">
			<div className="rounded-md bg-gray-50 p-2">
				<div className="flex items-center gap-2">
					<div className="flex flex-1 items-center gap-2 overflow-hidden">
						<span className="font-semibold whitespace-nowrap text-gray-900">Current:</span>
						<p className="overflow-hidden leading-normal text-ellipsis whitespace-nowrap text-gray-700">
							{input}
						</p>
					</div>
				</div>
			</div>

			<hr className="mt-4 mb-2 h-px w-full border-none bg-neutral-2" />

			<div ref={scrollRef} className="overflow-auto" style={{ maxHeight: height, minHeight: "auto" }}>
				{rephrasedData.map((item, index) => (
					<RephraseBlock
						key={index}
						rephrasedData={item}
						onInsert={onInsert}
						onRetry={handleRetry}
						onDismiss={() => handleRephraseDismiss(index)}
						retryDisabled={retryDisabled}
						rephraseOptionsCount={rephrasedData.length}
						isExpanded={isExpanded}
					/>
				))}
			</div>

			<div className={isExpanded ? "w-fit" : "w-full"}>
				<ButtonStandard
					onClick={handleDismiss}
					color="green"
					dangerouslySetClassName={`mt-2 ${isExpanded ? "w-fit" : "w-full"}`}
					data-testid="dismiss-rephrase-button"
				>
					Dismiss
				</ButtonStandard>
			</div>
		</div>
	);
};
